import { z } from 'zod'

/**
 * Quellenregister und Lizenzklassen (RNA M6).
 *
 * Die Lizenzklasse ist der einzige geschlossene Wertebereich hier, und sie ist
 * es aus einem Grund: an ihr haengt, ob ein Dokument ueberhaupt eingelesen
 * wird und welche Schicht es erreichen darf. Der Quellentyp bleibt bewusst
 * freier Text — das Vokabular der Herausgeber ist offen, und ein zu enges
 * Enum wuerde eine echte Quelle am Eintrag hindern.
 *
 * Das Register ist lesend. Geschrieben wird es beim Ingest aus dem Frontmatter
 * der Datei; die Datei bleibt die Wahrheit. Ein zweiter Schreibweg waere eine
 * zweite Wahrheit ueber dieselbe Lizenz.
 */

export const lizenzklasseSchema = z.enum(['frei', 'lizenziert_derivat', 'gesperrt'])
export type LizenzklasseValue = z.infer<typeof lizenzklasseSchema>

/** Der Nachweis je Derivat — die sechs Angaben, die eine spaetere Pruefung liest. */
export const kbNachweisSchema = z.object({
  /** Die Fragen, die das Dokument beantworten koennen muss (G2, aufgabenbezogene Auswahl). */
  aufgabenliste: z.array(z.string()),
  quelle_umfang_seiten: z.number().int().nonnegative().nullable(),
  derivat_umfang_woerter: z.number().int().nonnegative().nullable(),
  /** Wie im Frontmatter geschrieben, z.B. "2.4 %". */
  verdichtungsgrad: z.string().nullable(),
  /** Laengste mit der Quelle deckungsgleiche Wortfolge (G1). */
  laengste_wortfolge: z.number().int().nonnegative().nullable(),
  /** true, wenn `laengste_wortfolge` die Grenze 7 ueberschreitet. */
  wortlautgrenze_verletzt: z.boolean(),
})
export type KbNachweisDaten = z.infer<typeof kbNachweisSchema>

/** Eine Zeile im Quellenregister. */
export const kbQuelleSchema = z.object({
  path: z.string(),
  titel: z.string(),
  herausgeber: z.string().nullable(),
  quellen_typ: z.string().nullable(),
  bezugs_url: z.string().nullable(),
  abrufdatum: z.string().nullable(),
  tier: z.number().int().min(1).max(3).nullable(),
  /**
   * null heisst: die Quelle traegt keine erklaerte Klasse und ist deshalb
   * nicht eingelesen worden. Kein Standardwert, keine Vorbelegung.
   */
  lizenzklasse: lizenzklasseSchema.nullable(),
  fassung: z.string().nullable(),
  gueltig_von: z.string().nullable(),
  gueltig_bis: z.string().nullable(),
  /** `active` | `disabled` | `deleted` | `error` — aus `kb_sources`. */
  status: z.string(),
  /** Bei `error` der deutsche Grund, z.B. die fehlende Lizenzklasse. */
  error_reason: z.string().nullable(),
  chunks_count: z.number().int().nonnegative(),
  /** true bei `gesperrt`: es existiert nur die Karte, kein Inhalt. */
  nur_fundstellenkarte: z.boolean(),
  freigabe_status: z.string().nullable(),
  last_ingested_at: z.string(),
})
export type KbQuelle = z.infer<typeof kbQuelleSchema>

export const kbQuellenListResponseSchema = z.object({
  quellen: z.array(kbQuelleSchema),
  /**
   * true, wenn dieser Workspace eine Wissensbibliothek ist (028) — also die
   * geteilte Schicht. Dann kann hier kein lizenziertes Derivat liegen, und die
   * Oberflaeche sagt das, bevor jemand es versucht.
   */
  ist_wissensbibliothek: z.boolean(),
})
export type KbQuellenListResponse = z.infer<typeof kbQuellenListResponseSchema>

export const kbQuelleDetailResponseSchema = z.object({
  quelle: kbQuelleSchema,
  nachweis: kbNachweisSchema,
  /** Nur bei `gesperrt` gefuellt: die Fundstellenkarte als lesbarer Block. */
  fundstellenkarte: z.array(z.object({ feld: z.string(), wert: z.string() })),
})
export type KbQuelleDetailResponse = z.infer<typeof kbQuelleDetailResponseSchema>
